// Shared classification of wallet / RPC errors thrown by on-chain transactions
// (deposit, approve, spot deposit, etc.) so every flow shows a consistent,
// non-misleading message instead of a raw viem/RPC dump.
//
// NOTE on error codes: JSON-RPC code -32603 ("internal error") is overloaded —
// some wallets use it for user-denial, but Monad's RPC also returns it for
// "Signer had insufficient balance" (gas). So we classify by the error TEXT, not
// the numeric code, to avoid mislabeling a gas failure as a cancellation.

import {
  getCurrentChainConfig,
  getNativeSymbol,
} from "../config/chains/getters";

/**
 * ONE global, chain-aware "not enough gas" message used by every on-chain flow.
 * It reads the CURRENT chain's real native token symbol (MON, ETH, …) and its
 * native token symbol, so it never hardcodes "MON" or suggests a funding source
 * that is not available in the current UI.
 */
export const getInsufficientGasMessage = (): string => {
  const symbol = getNativeSymbol();
  return `Not enough ${symbol} to cover the network fee. Add ${symbol} to your wallet, then try again.`;
};

const collectErrorText = (error: any): string =>
  [
    error?.message,
    error?.data?.message,
    error?.details,
    error?.shortMessage,
    error?.reason,
    error?.error?.message,
  ]
    .filter((v) => typeof v === "string")
    .join(" ")
    .toLowerCase();

/** The user dismissed the wallet prompt (rejected the signature or transaction). */
export const isUserRejectedTxError = (error: any): boolean => {
  const text = collectErrorText(error);
  return (
    error?.code === 4001 ||
    error?.code === "ACTION_REJECTED" ||
    text.includes("user rejected") ||
    text.includes("user denied") ||
    text.includes("rejected the request") ||
    text.includes("request cancelled")
  );
};

/**
 * The wallet signed with a transaction nonce the node has already used.
 *
 * Nothing in this app sets a transaction nonce — every `nonce` in our code is an
 * EIP-712 relayer nonce, and the one-click agent wallet signs without a provider
 * — so this always comes from the WALLET's own nonce tracking being out of step
 * with the chain. On a testnet that is usually a chain reset, a dropped or
 * replaced pending transaction, or the wallet reusing a nonce after being
 * offline.
 *
 * It is worth naming precisely because the node REJECTS such a transaction:
 * nothing was submitted, so telling the trader that is true and lets them retry
 * safely. It also must never be auto-retried — a nonce error resolved by
 * resubmitting is exactly how a trade gets placed twice.
 */
export const isStaleNonceError = (error: any): boolean => {
  const text = collectErrorText(error);
  return (
    error?.code === "NONCE_EXPIRED" ||
    text.includes("nonce too low") ||
    text.includes("nonce is too low") ||
    (text.includes("nonce") && text.includes("lower than the current nonce"))
  );
};

/**
 * What to actually do about it, written for the person who sees it.
 *
 * This message has now been wrong twice, in opposite directions, and both
 * failures were watched live on Arc.
 *
 * The FIRST version named one wallet and one remedy — "MetaMask: Settings →
 * Advanced → Clear activity tab data". The trader did exactly that, retried, and
 * hit the identical error. Two reasons: Rabby reports `isMetaMask: true`, so
 * with both installed you can clear the wallet that did not sign; and clearing a
 * local cache cannot help when the wallet's own node is simply behind (the chain
 * reported nonce 110 for latest AND pending, nothing stuck, healthy gas, while
 * the wallet kept submitting below it).
 *
 * The SECOND version fixed the accuracy and broke the audience. It explained RPC
 * endpoints, extension conflicts and transaction numbering to retail traders,
 * who cannot act on any of it. A technically complete message that its reader
 * cannot use is not a better message.
 *
 * So: lead with the reassurance that matters (nothing was submitted, no funds
 * moved), give ONE concrete action anybody can perform, and route the residue to
 * support rather than asking a trader to audit their RPC configuration. The
 * network-switch step is first because it costs two taps and refreshes the
 * wallet's view; clearing activity data is the fallback; support covers the case
 * neither fixes, which is exactly the case that needs a human anyway.
 *
 * The diagnosis for that last case is preserved here rather than in the UI: if a
 * trader reports this after both steps, check whether their wallet's network
 * endpoint for this chain differs from the app's and is lagging.
 */
export const getStaleNonceMessage = (): string => {
  let network = "the network";
  try {
    network = getCurrentChainConfig()?.name || network;
  } catch {
    /* config unavailable — the generic wording still reads correctly */
  }
  return (
    `Your wallet is out of sync with ${network}, so your order was not placed. ` +
    "Nothing was submitted and no funds moved. " +
    `Open your wallet, switch to any other network, then switch back to ${network} and try again. ` +
    "If it still fails, clear your wallet's activity data (MetaMask: Settings → Advanced → Clear activity tab data), " +
    "then contact support from the Help menu if the problem continues."
  );
};

/**
 * The signer can't pay the network gas fee. On these flows the TOKEN amount is
 * validated before submit, so an "insufficient balance/funds" at send-time means
 * the wallet lacks the native gas token (e.g. MON on Monad) — NOT the deposit
 * token. Gives the user a specific gas-balance error instead of a generic failure.
 */
export const isInsufficientGasError = (error: any): boolean => {
  const text = collectErrorText(error);
  return (
    text.includes("insufficient balance") ||
    text.includes("insufficient funds") ||
    text.includes("signer had insufficient")
  );
};
